import { chordAt } from "@bandstand/core/arrangeChords";
import { isChord } from "@bandstand/core/chord";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import styled from "styled-components";

import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { Button, IconButton } from "../ui/controls";
import { Icon } from "../ui/Icon";
import { SheetView } from "../sheet/SheetView";
import { BassNotes } from "./BassNotes";
import { SaveDialog } from "./SaveDialog";
import { useArrangeGestures } from "./useArrangeGestures";

/**
 * Arranging the chords of a chord sheet by touch: they wiggle, and can be dragged to another word or onto the bin,
 * or tapped and then put where they go. A picked chord can get a bass note. These are real changes, saved in one of
 * three ways.
 */
export const ArrangeScreen = observer(function ArrangeScreen({ chordSheetId }: { chordSheetId: string }) {
    const { editor, library, display } = useStores();
    const [saving, setSaving] = useState(false);
    const loaded = editor.chordSheetId === chordSheetId;
    const gestures = useArrangeGestures({ move: (chord, to) => editor.moveChord(chord, to), remove: (chord) => editor.removeChord(chord) });
    const { dragging, caret } = gestures;
    // Read from the text, so it shows a new bass note straight away.
    const picked = gestures.picked && chordAt(editor.text, gestures.picked.ref);

    const hint = picked
        ? `Tap the lyrics to move ${picked} there, or the bin to remove it.`
        : "Drag a chord to another spot in the lyrics, or tap it and then where it goes.";

    useEffect(() => {
        void editor.load(chordSheetId);
    }, [editor, chordSheetId]);

    function undo() {
        gestures.unpick();
        editor.undo();
    }

    function cancel() {
        if (editor.changed && !confirm("Stop editing? Your changes won't be saved.")) return;

        editor.cancel();
    }

    return (
        <Screen>
            <Bar>
                <Button $kind="quiet" onClick={cancel}>
                    Cancel
                </Button>
                <Heading>
                    <Title>Editing {library.title(chordSheetId)}</Title>
                    <Subtitle>{hint}</Subtitle>
                </Heading>
                <IconButton aria-label="Undo" disabled={editor.undos.length === 0} onClick={undo}>
                    <Icon name="undo" />
                </IconButton>
                <Button disabled={!loaded} onClick={() => editor.switchTo("edit")}>
                    <Icon name="edit" size={18} />
                    <Wide>Edit as text</Wide>
                </Button>
                <Button $kind="primary" disabled={!loaded || !editor.changed} onClick={() => setSaving(true)}>
                    Done…
                </Button>
            </Bar>
            <Main {...gestures.handlers}>
                <Tray>
                    {picked && gestures.picked && isChord(picked) && (
                        <BassNotes
                            chord={picked}
                            songKey={editor.preview.key}
                            onChange={(bass) => editor.setBass(gestures.picked!.ref, bass)}
                        />
                    )}
                    <Bin data-bin aria-disabled={!picked} $over={gestures.overBin} $ready={!!picked || !!dragging}>
                        <Icon name="trash" />
                        <span>{picked ? `Remove ${picked}` : "Drop here to remove"}</span>
                    </Bin>
                </Tray>
                <Sheet data-sheet>
                    {loaded &&
                        (editor.preview.sections.length === 0 ? (
                            <Empty>There are no lyrics to put chords on yet. Choose Edit as text to add them.</Empty>
                        ) : (
                            <SheetView
                                columns
                                splitSections={display.splitSections}
                                sheet={editor.preview}
                                lyricsOnly={false}
                                textSize={display.textSize * 0.9}
                                arranging={gestures.arranging}
                            />
                        ))}
                </Sheet>
            </Main>
            {caret && <Cursor style={{ left: caret.x, top: caret.top, height: caret.height }} />}
            {dragging && <Ghost style={{ left: dragging.x, top: dragging.y }}>{dragging.name}</Ghost>}
            {saving && <SaveDialog onClose={() => setSaving(false)} />}
        </Screen>
    );
});

const Screen = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const Bar = styled.header`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    padding-top: max(6px, env(safe-area-inset-top));
    border-bottom: 1px solid ${color.border};
`;

const Heading = styled.div`
    flex: 1;
    min-width: 0;
    text-align: center;
`;

const Title = styled.h1`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Subtitle = styled.div`
    font-size: 13px;
    color: ${color.textMuted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Wide = styled.span`
    @media (max-width: 699px) {
        display: none;
    }
`;

/** The tray is on the left on a tablet, and at the bottom on a phone. */
const Main = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column-reverse;
    user-select: none;
    -webkit-user-select: none;

    @media (min-width: 700px) {
        flex-direction: row;
    }
`;

const Tray = styled.aside`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 10px 12px;
    padding-bottom: max(10px, env(safe-area-inset-bottom));
    border-top: 1px solid ${color.border};
    background: ${color.panel};

    @media (min-width: 700px) {
        width: 172px;
        border-top: 0;
        border-right: 1px solid ${color.border};
    }
`;

const Bin = styled.button<{ $over: boolean; $ready: boolean }>`
    flex-shrink: 0;
    min-height: 56px;
    padding: 10px;
    border: 2px dashed ${({ $over, $ready }) => ($over ? color.danger : $ready ? color.borderStrong : color.border)};
    border-radius: 14px;
    background: ${({ $over }) => ($over ? color.dangerSoft : "transparent")};
    color: ${({ $ready }) => ($ready ? color.danger : color.textFaint)};
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;

    @media (min-width: 700px) {
        margin-top: auto;
        min-height: 110px;
        flex-direction: column;
        text-align: center;
    }
`;

const Sheet = styled.main`
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 12px 16px 32px;

    @media (min-width: 700px) {
        padding: 16px 40px 40px;
    }
`;

const Empty = styled.p`
    font-size: 17px;
    color: ${color.textSoft};
`;

/** Where a dragged chord lands: a line between two letters. */
const Cursor = styled.div`
    position: fixed;
    z-index: 19;
    width: 2px;
    margin-left: -1px;
    border-radius: 1px;
    background: ${color.accent};
    pointer-events: none;
`;

/** The chord under the finger while it's dragged, a little above it so the finger doesn't hide it. */
const Ghost = styled.div`
    position: fixed;
    z-index: 20;
    transform: translate(-50%, -130%);
    padding: 4px 12px;
    border-radius: 10px;
    background: ${color.accent};
    color: ${color.onAccent};
    font-size: 20px;
    font-weight: 700;
    box-shadow: 0 8px 20px ${color.shadow};
    pointer-events: none;
`;
